import React, { useEffect, useRef, useState } from "react";
import AppProviders from "./AppProviders";
import AppRoutes from "./AppRoutes";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { ScreenOrientation } from "@capacitor/screen-orientation";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { parseQRUrl, validateQRToken } from "./services/gymLinkService";
import {
  logAppOpened,
  logSessionEnd,
  flushAnalyticsQueue,
} from "./services/analyticsService";
import TutorialOverlay from "./components/tutorial/TutorialOverlay";
import { useTutorial } from "./contexts/TutorialContext.jsx";
import LiveWorkoutHost from "./features/treinos/components/LiveWorkoutHost.jsx";
import { EVENTO_VINCULO_EQUIPE, vinculoDeuCerto } from "./utils/teamJoinStorage.js";
import AppUpdateModal from "./components/common/AppUpdateModal.jsx";
import ConectividadeHost from "./telemetria/ConectividadeHost.jsx";
import ErrorBoundary from "./components/common/ErrorBoundary.jsx";
import { fecharOffcanvasDoTopo } from "./utils/offcanvasStack";
import { buscarCriador, guardarCupom, normalizarCodigo } from "./features/criador/cupomCriador";
import { codigoDaUrl, guardarConvite } from "./features/clubes/conviteDeClube";
import { PONTOS_FIXOS } from "./features/competicao/pontosDaCompeticao";
import { abrirTelaGlobal } from "./utils/abrirTelaGlobal";

// Inscreve o usuário na equipe (deferred deep link) via a MESMA CF do referral —
// idempotente no gym. Já estando em outro time da mesma competição, o servidor
// TROCA e migra os pontos (o antigo guard "team_locked" foi invertido).
// Best-effort/silencioso.
async function applyTeamJoin(referralCode, teamCode) {
  if (!referralCode || !teamCode) return null;
  try {
    const fns = getFunctions(undefined, "southamerica-east1");
    const res = await httpsCallable(fns, "applyGymReferralToUser")({
      referralCode,
      origin: "GYM",
      teamCode,
    });
    // Devolve o resultado da equipe (status + nome + cor) p/ a UI de confirmação.
    return res?.data?.team || null;
  } catch (err) {
    console.warn("[DeferredTeam] falha silenciosa ao inscrever equipe:", err?.message);
    return {status: "error"};
  }
}

// Plugin nativo (iOS) — lê um link de referral copiado para o clipboard pela
// landing page, usado como deferred deep link em instalações novas.
const ClipboardReferralNative = registerPlugin("ClipboardReferral");
// Plugin nativo (Android) — lê o parâmetro referrer gravado pela Play Store
// no momento da instalação, usado como deferred deep link.
const InstallReferrerNative = registerPlugin("InstallReferrer");

// Programa Criadores Parceiros: código de criador vindo de link, área de
// transferência (iOS) ou Install Referrer (Android). Só guarda se o backend
// confirmar que o código é de um criador — um /ref/{academia} sem token não
// pode virar prefill da etapa do código.
async function guardarCodigoDeCriador(codigo) {
  const c = normalizarCodigo(codigo);
  if (!c) return;
  const dono = await buscarCriador(c);
  if (dono?.VALIDO) guardarCupom(dono.CODIGO, { nome: dono.NOME, foto: dono.FOTO_URL, origem: "app_link" });
}

async function guardarCodigoDeCriadorDoLink(url) {
  try {
    const m = new URL(url).pathname.match(/^\/ref\/([A-Za-z0-9]{3,24})\/?$/);
    if (m) await guardarCodigoDeCriador(m[1]);
  } catch { /* URL malformada: ignora */ }
}


// const TutorialTestButton = () => {
//   const { startTutorial } = useTutorial();

//   const testSteps = [
//     {
//       targetId: "tutorial-teste",
//       title: "Esse é um teste",
//       description: "Aqui você consegue visualizar como o tutorial aparece na tela.",
//     },
//   ];

//   return (
//     <button
//       data-tutorial-id="tutorial-teste"
//       onClick={() => startTutorial(testSteps)}
//       style={{
//         position: "fixed",
//         bottom: 20,
//         right: 20,
//         zIndex: 999999,
//         padding: "12px 16px",
//         borderRadius: 12,
//         border: "none",
//         background: "#FBBC04",
//         color: "#00095C",
//         fontWeight: 700,
//       }}
//     >
//       Testar Tutorial
//     </button>
//   );
// };



const App = () => {
  const isNative = Capacitor.isNativePlatform();
  const sessionStartRef = useRef(Date.now());
  const [deferredRefLinked, setDeferredRefLinked] = useState(false);
  const deferredRefRanRef = useRef(false);
  const deferredRefAndroidRanRef = useRef(false);
  // Resultado do vínculo de equipe por QR → modal de confirmação p/ o usuário.
  const [teamJoinResult, setTeamJoinResult] = useState(null);

  // Loga abertura do app — se não autenticado ainda, fica na fila local
  useEffect(() => {
    logAppOpened();
  }, []);

  // Assim que o Firebase Auth resolver o usuário, drena a fila de eventos pré-login
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getAuth(), (user) => {
      if (user) {
        flushAnalyticsQueue();
      }
    });
    return () => unsubscribe();
  }, []);

  // Bloqueia rotação em portrait
  useEffect(() => {
    if (!isNative) return;
    const lockPortrait = async () => {
      try {
        await ScreenOrientation.lock({ orientation: "portrait" });
      } catch (error) {
        console.warn("[ScreenOrientation] não foi possível travar em portrait:", error);
      }
    };
    lockPortrait();
  }, [isNative]);

  // Loga fim de sessão quando app vai para background
  useEffect(() => {
    if (!isNative) return;
    let listenerHandle;
    const handler = (state) => {
      if (!state.isActive) {
        const duration = Math.floor((Date.now() - sessionStartRef.current) / 1000);
        logSessionEnd(duration, []);
        sessionStartRef.current = Date.now();
      }
    };
    CapApp.addListener("appStateChange", handler).then((handle) => {
      listenerHandle = handle;
    });
    return () => listenerHandle?.remove();
  }, [isNative]);

  // Botão voltar do Android. Sem listener, o Capacitor faz `history.back()`
  // por conta própria — o que fecha uma sub-tela do AppShell (popstate) por
  // BAIXO de um offcanvas aberto, ou sai do app com o offcanvas na cara.
  // Com o listener, quem decide é a pilha: fecha o offcanvas do topo se
  // houver; senão, reproduz o padrão (volta no histórico ou sai do app).
  useEffect(() => {
    if (!isNative || Capacitor.getPlatform() !== "android") return;
    let listenerHandle;
    CapApp.addListener("backButton", ({ canGoBack }) => {
      if (fecharOffcanvasDoTopo()) return;
      if (canGoBack) window.history.back();
      else CapApp.exitApp();
    }).then((handle) => {
      listenerHandle = handle;
    });
    return () => listenerHandle?.remove();
  }, [isNative]);

  // Deep link QR
  useEffect(() => {
    if (!isNative) return;
    let listenerHandle;
    const setup = async () => {
      listenerHandle = await CapApp.addListener("appUrlOpen", async ({ url }) => {
        // Convite de clube (lesvita.com/c/CODIGO): só guarda. A plataforma
        // abre o clube — agora, se já estiver montada, ou depois do login.
        if (codigoDaUrl(url)) {
          guardarConvite(codigoDaUrl(url));
          return;
        }
        const isRefLink = url.includes("/ref/");
        const hasNewFormat = isRefLink && url.includes("join_token=");
        const hasLegacyFormat = url.includes("/join") && url.includes("token=");
        // Link/pôster de equipe: /ref/{gym}?team={code}, sem join_token. Precisa
        // ser tratado aqui também — antes caía no return e a equipe nunca era
        // aplicada em quem já tinha o app instalado.
        const hasTeamLink = isRefLink && url.includes("team=");
        // Link de Criador Parceiro (/ref/{CODIGO}, sem token nem equipe): guarda
        // o código para a etapa "Tem um código de criador?" do cadastro.
        if (isRefLink && !hasNewFormat && !hasTeamLink) {
          guardarCodigoDeCriadorDoLink(url);
          return;
        }
        if (!hasNewFormat && !hasLegacyFormat && !hasTeamLink) return;
        try {
          const { tokenId, gymCode, teamCode } = parseQRUrl(url);
          const uid = getAuth().currentUser?.uid;
          if (!uid) {
            sessionStorage.setItem("lesvita_pending_qr", url);
            return;
          }
          // join_token (premium/vínculo single-use) quando houver…
          if (tokenId) await validateQRToken(tokenId, uid);
          // …e/ou inscrição na equipe (vincula o gym também, idempotente).
          // Mostra modal de confirmação com o resultado (nome/cor da equipe).
          if (teamCode) {
            const teamRes = await applyTeamJoin(gymCode, teamCode);
            if (teamRes) setTeamJoinResult(teamRes);
          }
        } catch (err) {
          console.warn("[QR DeepLink] falha silenciosa:", err.message);
        }
      });
    };
    setup();
    return () => listenerHandle?.remove();
  }, [isNative]);

  // Deferred deep link via clipboard — instalação nova vinda da landing page (iOS).
  // Espelha o appUrlOpen acima: mesmo parseQRUrl + validateQRToken — só que a URL
  // vem do clipboard (gravada pelo ReferralLanding) em vez do evento appUrlOpen do SO.
  useEffect(() => {
    if (!isNative || Capacitor.getPlatform() !== "ios") return;
    if (localStorage.getItem("deferred_ref_processed")) return;

    const unsubscribe = onAuthStateChanged(getAuth(), async (user) => {
      if (!user || deferredRefRanRef.current) return;
      deferredRefRanRef.current = true;

      try {
        const { url } = await ClipboardReferralNative.readReferralFromClipboard();
        // Convite de clube copiado pela página /c/ antes da loja: a plataforma
        // entra no clube assim que montar (conviteDeClube.js).
        if (url && codigoDaUrl(url)) {
          guardarConvite(codigoDaUrl(url));
          return;
        }

        const userSnap = await getDoc(doc(getFirestore(), "USER", user.uid));
        const alreadyLinked = userSnap.exists() && userSnap.data()?.GYM_REFERRAL?.APPLIED === true;
        if (alreadyLinked) return;

        if (!url) return;

        // Link de Criador Parceiro copiado pela página do link: sem join_token.
        if (!url.includes("join_token=")) {
          await guardarCodigoDeCriadorDoLink(url);
          return;
        }

        const { tokenId, gymCode, teamCode } = parseQRUrl(url);
        await validateQRToken(tokenId, user.uid);

        // Carrega a equipe junto do gym (aditivo). Enrollment idempotente no gym.
        if (teamCode) {
          const teamRes = await applyTeamJoin(gymCode, teamCode);
          if (teamRes) setTeamJoinResult(teamRes);
        }

        setDeferredRefLinked(true);
        setTimeout(() => setDeferredRefLinked(false), 4000);
      } catch (err) {
        console.warn("[DeferredRef] falha silenciosa:", err.message);
      } finally {
        localStorage.setItem("deferred_ref_processed", "1");
      }
    });

    return () => unsubscribe();
  }, [isNative]);

  // Deferred deep link via Play Install Referrer — instalação nova vinda da landing page (Android).
  // Espelha o fluxo iOS acima: mesmo validateQRToken, chave localStorage separada.
  useEffect(() => {
    if (!isNative || Capacitor.getPlatform() !== "android") return;
    if (localStorage.getItem("deferred_ref_android_processed")) return;

    const unsubscribe = onAuthStateChanged(getAuth(), async (user) => {
      if (!user || deferredRefAndroidRanRef.current) return;
      deferredRefAndroidRanRef.current = true;

      let shouldMark = false;
      try {
        // O plugin devolve joinToken; pode expor também o referrer cru
        // (join_token=…&gym=…&team=…). Lemos team/gym defensivamente p/ carregar a
        // equipe junto — sem quebrar caso o plugin ainda não exponha esses campos.
        const referrerRes = await InstallReferrerNative.getInstallReferrer();
        const { joinToken, referrer } = referrerRes || {};

        // Convite de clube (página /c/ → Play Store com referrer=clube=CODIGO).
        const codigoClube = typeof referrer === "string"
          ? new URLSearchParams(referrer).get("clube") : null;
        if (codigoClube && guardarConvite(codigoClube)) {
          shouldMark = true;
          return;
        }

        const userSnap = await getDoc(doc(getFirestore(), "USER", user.uid));
        if (userSnap.exists() && userSnap.data()?.GYM_REFERRAL?.APPLIED === true) {
          shouldMark = true;
          return;
        }

        // Programa Criadores Parceiros: referrer=criador=CODIGO (sem join_token).
        const codigoCriador = referrerRes?.criador ||
          (typeof referrer === "string" ? new URLSearchParams(referrer).get("criador") : "");
        if (!joinToken && codigoCriador) {
          shouldMark = true;
          await guardarCodigoDeCriador(codigoCriador);
          return;
        }
        if (!joinToken) return; // referrer vazio — não marcar, tentar no próximo cold start

        // Deriva team/gym do objeto do plugin ou do referrer string, se houver.
        let teamCode = referrerRes?.team || null;
        let gymCode = referrerRes?.gym || null;
        if ((!teamCode || !gymCode) && typeof referrer === "string") {
          const rp = new URLSearchParams(referrer);
          teamCode = teamCode || rp.get("team");
          gymCode = gymCode || rp.get("gym");
        }

        shouldMark = true;
        await validateQRToken(joinToken, user.uid);

        // Carrega a equipe junto do gym (aditivo). Enrollment idempotente no gym.
        if (teamCode && gymCode) {
          const teamRes = await applyTeamJoin(gymCode, teamCode);
          if (teamRes) setTeamJoinResult(teamRes);
        }

        setDeferredRefLinked(true);
        setTimeout(() => setDeferredRefLinked(false), 4000);
      } catch (err) {
        console.warn("[DeferredRef Android] falha silenciosa:", err.message);
      } finally {
        if (shouldMark) localStorage.setItem("deferred_ref_android_processed", "1");
      }
    });

    return () => unsubscribe();
  }, [isNative]);

  // Modal de confirmação de equipe disparado por outros caminhos (ex.: QR lido
  // deslogado e aplicado após login em PlataformaPessoal) via evento global.
  useEffect(() => {
    const onTeamResult = (e) => { if (e?.detail) setTeamJoinResult(e.detail); };
    window.addEventListener(EVENTO_VINCULO_EQUIPE, onTeamResult);
    return () => window.removeEventListener(EVENTO_VINCULO_EQUIPE, onTeamResult);
  }, []);

  return (
    <AppProviders>
      {/* Erro de render em qualquer rota vira "algo deu errado + recarregar"
          em vez de tela preta — ver ErrorBoundary.jsx. */}
      <ErrorBoundary>
        <AppRoutes />
      </ErrorBoundary>
      {/* Sessão de treino ao vivo (global): ModoTreino + widget + check-in */}
      <LiveWorkoutHost />
      {/* <TutorialTestButton /> */}
      <TutorialOverlay />
      <AppUpdateModal />
      {/* "É a sua internet, não o app": sem conexão / conexão instável */}
      <ConectividadeHost />
      {!isNative && <SpeedInsights />}
      {deferredRefLinked && (
        <div style={{
          position: "fixed", bottom: 28, left: "50%", transform: "translateX(-50%)",
          background: "#1f9d55", color: "#fff", padding: "10px 20px", borderRadius: 12,
          fontSize: 14, fontWeight: 600, zIndex: 999999, boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
          textAlign: "center",
        }}>
          Vinculado à sua academia automaticamente ✓
        </div>
      )}
      <TeamJoinModal result={teamJoinResult} onClose={() => setTeamJoinResult(null)} />
    </AppProviders>
  );
};

// ─── Modal de confirmação de vínculo de equipe (QR) ────────────────────────────
// Mostra ao usuário o resultado da leitura do QR de equipe: sucesso (nome + cor),
// já-vinculado, ou motivo de não ter entrado. Sem isto o vínculo era 100% silencioso.
const TEAM_FALLBACK_COLOR = "#6C5CE7";

function teamJoinCopy(result) {
  const name = result?.teamName || "sua equipe";
  switch (result?.status) {
    case "linked":
      return {icon: "🎉", title: "Você entrou na equipe!",
        body: <>Agora você faz parte da equipe <strong>{name}</strong>. Seu XP conta para o time.</>};
    case "unchanged":
      return {icon: "✅", title: "Você já está nesta equipe",
        body: <>Você já faz parte da equipe <strong>{name}</strong>.</>};
    case "switched":
      // Troca por link: os pontos do usuário migram junto, então a confirmação
      // diz isso — é a dúvida imediata de quem acabou de trocar de time.
      return {icon: "🔄", title: "Você trocou de equipe!",
        body: <>Agora você faz parte da equipe <strong>{name}</strong>. Seus pontos foram
          junto com você.</>};
    case "team_locked":
      // Não acontece mais (o guard foi invertido para "switched"), mas o case
      // fica para apps antigos que ainda recebam a resposta de uma versão velha.
      return {icon: "🔒", title: "Você já está em outra equipe",
        body: <>Você já faz parte de uma equipe nesta competição. Fale com a academia para trocar de time.</>};
    case "equipe_nao_pertence_a_competicao":
      return {icon: "⚠️", title: "Link inválido para esta competição",
        body: <>Esse link não vale para a competição desta academia. Peça o link novo para a academia.</>};
    case "join_code_ambiguo":
      return {icon: "⚠️", title: "Código duplicado",
        body: <>Esse código está repetido na competição. A academia precisa corrigir antes de você entrar.</>};
    case "competicao_fechada":
      return {icon: "🏁", title: "Competição encerrada",
        body: <>Esta competição não está mais aberta para entrada.</>};
    case "no_active_team_competition":
      return {icon: "⏳", title: "Competição ainda não começou",
        body: <>Ainda não há uma competição de equipes ativa nesta academia. Tente novamente quando a academia iniciar.</>};
    case "team_not_found":
      return {icon: "⚠️", title: "Equipe não encontrada",
        body: <>Não encontramos essa equipe. Peça à academia para conferir o QR Code.</>};
    default:
      return {icon: "⚠️", title: "Não foi possível entrar na equipe",
        body: <>Algo deu errado ao ler o QR da equipe. Tente novamente ou fale com a academia.</>};
  }
}

function TeamJoinModal({result, onClose}) {
  // Sucesso tem dois passos: "entrou" → "como pontuar e divirta-se". Erro é
  // um passo só. O passo volta a 1 a cada resultado novo.
  const [passo, setPasso] = useState(1);
  useEffect(() => { setPasso(1); }, [result]);

  if (!result) return null;
  // Fonte única em utils/teamJoinStorage — esta lista já divergiu do backend uma
  // vez ("switched" faltando) e a troca virou "deu erro" para o usuário.
  const isSuccess = vinculoDeuCerto(result);
  const accent = result.teamColor || TEAM_FALLBACK_COLOR;
  const {icon, title, body} = teamJoinCopy(result);
  const nomeEquipe = result.teamName || "sua equipe";

  const verCompeticao = () => {
    onClose();
    abrirTelaGlobal("competicao", {competicao: null});
  };

  const botao = (rotulo, onClick) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: "100%", padding: "13px 0", borderRadius: 12, border: "none",
        background: isSuccess ? accent : "#333", color: "#fff",
        fontSize: 15, fontWeight: 700, cursor: "pointer",
      }}
    >
      {rotulo}
    </button>
  );

  const regra = (emoji, texto) => (
    <li style={{display: "flex", gap: 10, alignItems: "flex-start", textAlign: "left",
      fontSize: 14, lineHeight: 1.45, color: "#333"}}>
      <span style={{fontSize: 18, lineHeight: 1.2}}>{emoji}</span>
      <span>{texto}</span>
    </li>
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1000000, display: "flex",
        alignItems: "center", justifyContent: "center", padding: 24,
        background: "rgba(0,0,0,0.55)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 340, background: "#fff", borderRadius: 20,
          padding: "26px 22px 20px", textAlign: "center",
          boxShadow: "0 10px 40px rgba(0,0,0,0.35)",
          borderTop: isSuccess ? `6px solid ${accent}` : "none",
        }}
      >
        {isSuccess && passo === 2 ? (
          <>
            <div style={{fontSize: 40, marginBottom: 8}}>🏆</div>
            <h3 style={{fontSize: 18, fontWeight: 800, margin: "0 0 6px", color: "#1a1a1a"}}>
              Bem-vindo à equipe {nomeEquipe}!
            </h3>
            <p style={{fontSize: 14, lineHeight: 1.5, color: "#444", margin: "0 0 14px"}}>
              Tudo o que você registra soma pontos para o seu time:
            </p>
            <ul style={{listStyle: "none", padding: 0, margin: "0 0 14px",
              display: "flex", flexDirection: "column", gap: 10}}>
              {regra("💪", <>Treino registrado: <strong>+{PONTOS_FIXOS.TREINO} pts</strong> + o XP do treino</>)}
              {regra("🥗", <>Refeição registrada: <strong>+{PONTOS_FIXOS.DIETA} pts</strong></>)}
              {regra("⚡", <>Todo XP que você ganha no app vira <strong>1 ponto</strong></>)}
            </ul>
            <p style={{fontSize: 15, fontWeight: 800, color: "#1a1a1a", margin: "0 0 16px"}}>
              Divirta-se e leve sua equipe ao topo! 🚀
            </p>
            {botao("Ver competição", verCompeticao)}
          </>
        ) : (
          <>
            <div style={{fontSize: 40, marginBottom: 8}}>{icon}</div>
            {isSuccess && (
              <div style={{display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 10}}>
                <span style={{width: 18, height: 18, borderRadius: "50%", background: accent}} />
                <span style={{fontSize: 18, fontWeight: 800, color: "#1a1a1a"}}>{result.teamName || "Sua equipe"}</span>
              </div>
            )}
            <h3 style={{fontSize: 18, fontWeight: 800, margin: "0 0 8px", color: "#1a1a1a"}}>{title}</h3>
            <p style={{fontSize: 14, lineHeight: 1.5, color: "#444", margin: "0 0 18px"}}>{body}</p>
            {isSuccess ? botao("Próximo", () => setPasso(2)) : botao("Fechar", onClose)}
          </>
        )}
      </div>
    </div>
  );
}

export default App;
